<template>
	<div class="send-email">
		<el-card>
			<div slot="header">
				<span>提交邮件审核</span>
			</div>
			
			<el-form :model="form" :rules="rules" ref="form" label-width="100px">
				<el-form-item label="发件邮箱" prop="emailConfigId">
					<el-select v-model="form.emailConfigId" placeholder="请选择发件邮箱" style="width: 100%">
						<el-option 
							v-for="config in emailConfigs" 
							:key="config.id" 
							:label="`${config.email} (${getProviderName(config.provider)})`" 
							:value="config.id">
						</el-option>
					</el-select>
				</el-form-item>
				
				<!-- 收件人列表下拉已移除，改为下方按列表展开选择收件人 -->
				
								<el-form-item label="选择收件人" prop="selectedRecipients">
					<div class="recipient-selection">
						<div class="selection-header">
							<span class="selected-count">已选择 {{ form.selectedRecipients.length }} 人</span>
						</div>
						<div class="recipient-list" v-show="showRecipients">
							<el-collapse v-model="activePanels">
								<el-collapse-item 
									v-for="list in recipientLists" 
									:key="list.id" 
									:title="`${list.name}（${(list.recipients||[]).length}人）`" 
									:name="list.id">
									<el-checkbox-group v-model="form.selectedRecipients">
										<div v-for="r in (list.recipients||[])" :key="r.id" class="recipient-item">
											<el-checkbox :label="r.id">
												<div class="recipient-info">
													<span class="recipient-name">{{ r.name || '未命名' }}</span>
													<span class="recipient-email">{{ r.email }}</span>
												</div>
											</el-checkbox>
										</div>
									</el-checkbox-group>
								</el-collapse-item>
							</el-collapse>
						</div>
					</div>
				</el-form-item>
				
				<el-form-item label="收件人邮箱" prop="recipientEmails" v-if="form.selectedRecipients.length > 0">
					<el-input 
						type="textarea" 
						v-model="form.recipientEmails" 
						:rows="3"
						placeholder="收件人邮箱将自动填入"
						readonly>
					</el-input>
				</el-form-item>
				
				<el-form-item label="邮件主题" prop="subject">
					<el-input 
						v-model="form.subject" 
						placeholder="请输入邮件主题"
						@input="checkSensitiveWords">
					</el-input>
				</el-form-item>
				
				<el-form-item label="邮件内容" prop="content">
					<div class="content-editor">
						<div class="editor-toolbar">
							<el-button size="small" @click="addAttachment" icon="el-icon-paperclip">
								添加附件
							</el-button>
							<span class="attachment-tip">单个文件不超过10MB，所有附件总大小不超过10MB</span>
						</div>
						
						<!-- 敏感词检测提示 -->
						<div v-if="sensitiveWordReport" class="sensitive-word-alert">
							<el-alert
								:title="sensitiveWordReport.message"
								:type="sensitiveWordReport.status"
								:closable="false"
								show-icon>
								<div v-if="sensitiveWordReport.details && sensitiveWordReport.details.length > 0">
									<p><strong>检测到的敏感词：</strong></p>
									<div class="sensitive-word-list">
										<span 
											v-for="detail in sensitiveWordReport.details" 
											:key="detail.word"
											class="sensitive-word-tag">
											{{ detail.word }} ({{ detail.count }}次)
										</span>
									</div>
									<p class="sensitive-word-tip">
										<i class="el-icon-warning"></i>
										请修改以上敏感词后再发送邮件
									</p>
								</div>
							</el-alert>
						</div>
						
						<el-input 
							type="textarea" 
							v-model="form.content" 
							:rows="10"
							placeholder="请输入邮件内容"
							@input="checkSensitiveWords">
						</el-input>
						
						<!-- 内容安全状态指示器 -->
						<div class="content-safety-status">
							<div v-if="isChecking" class="checking-status">
								<i class="el-icon-loading"></i>
								正在检测内容安全性...
							</div>
							<div v-else-if="contentSafe" class="safe-status">
								<i class="el-icon-success"></i>
								内容安全，可以发送
							</div>
							<div v-else class="unsafe-status">
								<i class="el-icon-warning"></i>
								内容包含敏感词，请修改后发送
							</div>
						</div>
						
						<!-- 统一附件列表（包含原图片） -->
						<div v-if="(form.attachments && form.attachments.length > 0) || (form.images && form.images.length > 0)" class="attachment-list">
							<div class="attachment-title">已添加的附件：</div>
							<div v-for="(attachment, index) in mergedAttachments" :key="index" class="attachment-item">
								<i class="el-icon-document"></i>
								<span class="attachment-name">{{ attachment.name }}</span>
								<span class="attachment-size">({{ formatFileSize(attachment.size) }})</span>
								<el-button size="mini" type="danger" @click="removeMerged(index)" icon="el-icon-delete">
									删除
								</el-button>
							</div>
						</div>
					</div>
				</el-form-item>
				
				<el-form-item>
					<el-button 
						type="primary" 
						@click="sendEmail" 
						:loading="sending"
						:disabled="!canSend || !contentSafe">
						{{ sending ? '提交中...' : (isDraftMode ? '重新提交审核' : '提交审核') }}
					</el-button>
					<el-button @click="previewEmail">预览</el-button>
				</el-form-item>
			</el-form>
		</el-card>
		
		<!-- 隐藏的文件上传输入框 -->
		<input 
			ref="imageInput" 
			type="file" 
			accept="image/*" 
			style="display: none;" 
			@change="handleImageUpload" 
			multiple />
		<input 
			ref="attachmentInput" 
			type="file" 
			style="display: none;" 
			@change="handleAttachmentUpload" 
			multiple />
		
		<!-- 预览对话框 -->
		<el-dialog title="邮件预览" :visible.sync="previewVisible" width="800px">
			<div class="preview-content">
				<div class="preview-item">
					<strong>发件人：</strong>
					<span>{{ getSelectedConfigEmail() }}</span>
				</div>
				<div class="preview-item">
					<strong>收件人：</strong>
					<span>{{ form.selectedRecipients.length }} 人</span>
				</div>
				<div class="preview-item">
					<strong>收件人邮箱：</strong>
					<div class="preview-emails">{{ form.recipientEmails }}</div>
				</div>
				<div class="preview-item">
					<strong>主题：</strong>
					<span>{{ form.subject }}</span>
				</div>
				<div class="preview-item">
					<strong>内容：</strong>
					<div class="preview-text">{{ form.content }}</div>
				</div>
			</div>
		</el-dialog>
		
		<!-- 发送结果对话框 -->
		<el-dialog title="发送结果" :visible.sync="resultVisible" width="600px">
			<div class="result-content">
				<div class="result-item">
					<strong>总发送数：</strong>
					<span>{{ sendResult.totalCount }}</span>
				</div>
				<div class="result-item">
					<strong>成功数：</strong>
					<span class="success">{{ sendResult.successCount }}</span>
				</div>
				<div class="result-item">
					<strong>失败数：</strong>
					<span class="danger">{{ sendResult.failedCount }}</span>
				</div>
				<div class="result-item">
					<strong>发送时间：</strong>
					<span>{{ formatDate(sendResult.createdAt) }}</span>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
export default {
	name: 'SendEmail',
	data() {
		return {
			showRecipients: true,
			activePanels: [],
			form: {
				emailConfigId: null,
				selectedListIds: [],
				selectedRecipients: [],
				recipientEmails: '',
				subject: '',
				content: '',
				images: [],
				attachments: []
			},
			rules: {
				emailConfigId: [
					{ required: true, message: '请选择发件邮箱', trigger: 'change' }
				],
				selectedRecipients: [
					{ type: 'array', required: true, message: '请选择收件人', trigger: 'change' }
				],
				subject: [
					{ required: true, message: '请输入邮件主题', trigger: 'blur' }
				],
				content: [
					{ required: true, message: '请输入邮件内容', trigger: 'blur' }
				]
			},
			emailConfigs: [],
			recipientLists: [],
			sending: false,
			previewVisible: false,
			resultVisible: false,
			sendResult: {},
			selectAll: false,
			isIndeterminate: false,
			sensitiveWordReport: null, // 用于存储敏感词检测结果
			isChecking: false, // 用于控制敏感词检测的加载状态
			contentSafe: true, // 用于控制内容是否安全
			checkTimer: null // 用于防抖
		}
	},
	computed: {
		canSend() {
			return this.form.emailConfigId && 
				   this.form.selectedRecipients.length > 0 && 
				   this.form.subject && 
				   this.form.content &&
				   this.contentSafe;
		},
		isDraftMode() {
			return !!this.$route.query.draftId;
		},
		mergedAttachments() {
			const imgs = (this.form.images || []).map(x => ({ name: x.name, size: x.size || 0, file: x.file }));
			const atts = (this.form.attachments || []).map(x => ({ name: x.name, size: x.size || 0, file: x.file }));
			return [...imgs, ...atts];
		},
		currentListRecipients() {
			if (!this.form.selectedListIds || this.form.selectedListIds.length === 0) return [];
			// 汇总多个列表的收件人并去重
			const selectedIdSet = new Set(this.form.selectedListIds);
			const merged = [];
			const seen = new Set();
			for (const list of this.recipientLists) {
				if (!selectedIdSet.has(list.id)) continue;
				(list.recipients || []).forEach(r => {
					if (!seen.has(r.id)) { seen.add(r.id); merged.push(r); }
				});
			}
			return merged;
		}
	},
	mounted() {
		this.loadEmailConfigs();
		this.loadRecipientLists();
		this.initPrefillFromDraft();
	},
	watch: {
		'form.selectedRecipients': {
			handler(newVal) {
				this.updateRecipientEmails();
				this.updateSelectAllState();
			},
			deep: true
		}
	},
	methods: {
		async loadEmailConfigs() {
			try {
				const userId = this.$store.getters.userId;
				const response = await this.$http.get('/api/email-configs', {
					params: { userId: userId }
				});
				this.emailConfigs = response.data.filter(config => config.isActive);
			} catch (error) {
				console.error('加载邮箱配置失败:', error);
				this.$message.error('加载邮箱配置失败');
			}
		},
		
		async loadRecipientLists() {
			try {
				const userId = this.$store.getters.userId;
				const response = await this.$http.get('/api/recipient-lists/with-recipients', {
					params: { userId: userId }
				});
				this.recipientLists = response.data;
			} catch (error) {
				console.error('加载收件人列表失败:', error);
				this.$message.error('加载收件人列表失败');
			}
		},
		
		onListChange() {
			// 以前为多选列表时的处理逻辑，现按折叠面板选择，不再使用
		},
		
		onSelectAllChange() {},

		
		async initPrefillFromDraft() {
			const draftId = this.$route.query.draftId;
			if (!draftId) return;
			try {
				const detail = await this.$http.get(`/api/send-logs/${draftId}`);
				const log = detail.data || {};
				this.form.subject = log.subject || '';
				this.form.content = log.content || '';
				this.form.emailConfigId = log.emailConfigId || null;
				// 解析收件人
				if (log.recipientsInfo) {
					try {
						this.form.selectedRecipients = JSON.parse(log.recipientsInfo) || [];
						this.updateRecipientEmails();
					} catch (e) {}
				}
				// 解析附件/图片
				if (log.attachmentsInfo) {
					try {
						const info = JSON.parse(log.attachmentsInfo);
						const images = Array.isArray(info.images) ? info.images : [];
						const attachments = Array.isArray(info.attachments) ? info.attachments : [];
						// 将Base64恢复为可预览/重新提交的文件对象占位（不必真的File对象即可再次提交）
						this.form.images = images.map(x => ({
							name: x.name,
							size: x.size,
							type: x.contentType,
							url: `data:${x.contentType};base64,${x.contentBase64}`,
							// 重新提交时，前端无法还原原始File；交由后端从草稿ID读取Base64发送，或让用户可移除/增补
							file: null
						}));
						this.form.attachments = attachments.map(x => ({
							name: x.name,
							size: x.size,
							type: x.contentType,
							file: null
						}));
					} catch (e) {}
				}
			} catch (e) {
				this.$message.error('加载草稿失败');
			}
		},
		
		updateRecipientEmails() {
			// 以当前所有已选ID为准，从所有列表收件人里聚合邮箱
			const idSet = new Set(this.form.selectedRecipients);
			const emails = [];
			const seen = new Set();
			for (const list of this.recipientLists) {
				(list.recipients || []).forEach(r => {
					if (idSet.has(r.id) && !seen.has(r.email)) { seen.add(r.email); emails.push(r.email); }
				});
			}
			this.form.recipientEmails = emails.join(', ');
		},
		
		updateSelectAllState() {},

		
		previewEmail() {
			if (!this.canSend) {
				this.$message.warning('请先填写完整的邮件信息');
				return;
			}
			this.previewVisible = true;
		},
		
		async sendEmail() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					// 检查附件总大小
					if (!this.checkTotalAttachmentSize()) {
						return;
					}
					
					this.sending = true;
					try {
						// 构建FormData对象，支持文件上传
						const formData = new FormData();
						formData.append('emailConfigId', this.form.emailConfigId);
						formData.append('recipientIds', JSON.stringify(this.form.selectedRecipients));
						formData.append('subject', this.form.subject);
						formData.append('content', this.form.content);
						
						// 判断是否有新上传的文件
						const hasNewImages = (this.form.images || []).some(img => !!img.file);
						const hasNewAttachments = (this.form.attachments || []).some(att => !!att.file);
						const draftId = this.$route.query.draftId;
						
						// 仅上传真实文件
						(this.form.images || []).forEach(image => {
							if (image.file) {
								formData.append('images', image.file);
							}
						});
						(this.form.attachments || []).forEach(attachment => {
							if (attachment.file) {
								formData.append('attachments', attachment.file);
							}
						});
						
						// 草稿模式且未上传新文件，则复用原附件
						if (draftId && !hasNewImages && !hasNewAttachments) {
							formData.append('fromSendLogId', draftId);
						}
						
						// 使用审核流程API
						const response = await this.$http.post('/api/send/submit', formData, {
							headers: {
								'Content-Type': 'multipart/form-data'
							},
							timeout: 0,
							maxContentLength: Infinity,
							maxBodyLength: Infinity
						});
						this.sendResult = response.data;
						this.resultVisible = true;
						this.$message.success(this.isDraftMode ? '已重新提交审核，请等待审核' : '邮件已提交审核，请等待管理员审核通过后发送');
					} catch (error) {
						console.error('提交失败:', error);
						this.$message.error('提交失败，请检查邮箱配置和网络连接');
					} finally {
						this.sending = false;
					}
				}
			});
		},
		
		getProviderName(provider) {
			const names = {
				gmail: 'Gmail',
				qq: 'QQ邮箱',
				netease_163: '网易163',
				outlook: 'Outlook',
				netease_126: '网易126',
				yahoo: 'Yahoo邮箱',
				sina: '新浪邮箱'
			};
			return names[provider] || provider;
		},
		
		getSelectedConfigEmail() {
			const config = this.emailConfigs.find(c => c.id === this.form.emailConfigId);
			return config ? config.email : '';
		},
		
		formatDate(date) {
			if (!date) return '';
			return new Date(date).toLocaleString();
		},
		
		// 插入图片
		insertImage() {
			this.$refs.imageInput.click();
		},
		
		// 添加附件
		addAttachment() {
			this.$refs.attachmentInput.click();
		},
		
		// 展开/收起收件人区
		toggleRecipients() {
			this.showRecipients = !this.showRecipients;
		},
		
		// 处理图片上传
		handleImageUpload(event) {
			const files = event.target.files;
			const maxFileSize = 10 * 1024 * 1024; // 10MB
			
			for (let file of files) {
				if (file.type.startsWith('image/')) {
					// 检查单个文件大小
					if (file.size > maxFileSize) {
						this.$message.error(`图片 ${file.name} 超过10MB限制，无法上传`);
						continue;
					}
					
					const reader = new FileReader();
					reader.onload = (e) => {
						this.form.images.push({
							name: file.name,
							size: file.size,
							type: file.type,
							url: e.target.result,
							file: file
						});
						// 检查所有附件总大小
						this.checkTotalAttachmentSize();
					};
					reader.readAsDataURL(file);
				} else {
					this.$message.warning(`${file.name} 不是有效的图片文件`);
				}
			}
			// 清空input值，允许重复选择同一文件
			event.target.value = '';
		},
		
		// 处理附件上传
		handleAttachmentUpload(event) {
			const files = event.target.files;
			const maxFileSize = 10 * 1024 * 1024; // 10MB
			
			for (let file of files) {
				// 检查单个文件大小
				if (file.size > maxFileSize) {
					this.$message.error(`文件 ${file.name} 超过10MB限制，无法上传`);
					continue;
				}
				
				this.form.attachments.push({
					name: file.name,
					size: file.size,
					type: file.type,
					file: file
				});
			}
			
			// 检查所有附件总大小
			this.checkTotalAttachmentSize();
			
			// 清空input值，允许重复选择同一文件
			event.target.value = '';
		},
		
		// 删除图片
		removeMerged(index) {
			const totalImgs = (this.form.images || []).length;
			if (index < totalImgs) {
				this.form.images.splice(index, 1);
			} else {
				this.form.attachments.splice(index - totalImgs, 1);
			}
			// 重新检查总大小
			this.checkTotalAttachmentSize();
		},
		
		// 格式化文件大小
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
		},
		
		// 检查所有附件总大小
		checkTotalAttachmentSize() {
			const maxTotalSize = 10 * 1024 * 1024; // 10MB
			let totalSize = 0;
			
			// 计算图片总大小
			(this.form.images || []).forEach(img => {
				totalSize += img.size || 0;
			});
			
			// 计算附件总大小
			(this.form.attachments || []).forEach(att => {
				totalSize += att.size || 0;
			});
			
			if (totalSize > maxTotalSize) {
				this.$message.error(`所有附件总大小 ${this.formatFileSize(totalSize)} 超过10MB限制，请删除部分附件`);
				return false;
			}
			
			return true;
		},

		// 敏感词检测
		async checkSensitiveWords() {
			// 使用防抖，避免频繁检测
			if (this.checkTimer) {
				clearTimeout(this.checkTimer);
			}
			
			this.checkTimer = setTimeout(() => {
				this.performSensitiveWordCheck();
			}, 500);
		},
		
		// 执行敏感词检测
		performSensitiveWordCheck() {
			this.isChecking = true;
			
			// 导入敏感词检测服务
			import('@/utils/sensitiveWordFilter').then(module => {
				const { getSensitiveWordReport } = module;
				
				try {
					// 检测主题和内容
					const subjectReport = getSensitiveWordReport(this.form.subject || '');
					const contentReport = getSensitiveWordReport(this.form.content || '');
					
					// 合并检测结果
					if (subjectReport.hasSensitiveWords || contentReport.hasSensitiveWords) {
						const allSensitiveWords = [
							...subjectReport.sensitiveWords,
							...contentReport.sensitiveWords
						];
						
						this.sensitiveWordReport = {
							status: 'danger',
							message: `检测到敏感词，共 ${allSensitiveWords.length} 类`,
							details: allSensitiveWords.map(word => ({
								word: word,
								count: 1
							})),
							hasSensitiveWords: true
						};
						this.contentSafe = false;
					} else {
						this.sensitiveWordReport = {
							status: 'success',
							message: '内容安全，可以发送',
							hasSensitiveWords: false
						};
						this.contentSafe = true;
					}
				} catch (error) {
					console.error('敏感词检测失败:', error);
					this.sensitiveWordReport = {
						status: 'error',
						message: '敏感词检测失败，请稍后再试'
					};
					this.contentSafe = false;
				} finally {
					this.isChecking = false;
				}
			}).catch(error => {
				console.error('加载敏感词检测模块失败:', error);
				this.isChecking = false;
				this.contentSafe = true; // 如果模块加载失败，默认允许发送
			});
		}
	},
	beforeDestroy() {
		// 清理定时器
		if (this.checkTimer) {
			clearTimeout(this.checkTimer);
		}
	}
}
</script>

<style scoped>
.send-email {
	padding: 20px;
}

.preview-content {
	padding: 20px;
}

.preview-item {
	margin-bottom: 15px;
}

.preview-item strong {
	display: inline-block;
	width: 80px;
}

.preview-text {
	margin-top: 10px;
	padding: 15px;
	background-color: #f5f5f5;
	border-radius: 4px;
	white-space: pre-wrap;
}

.result-content {
	padding: 20px;
}

.result-item {
	margin-bottom: 15px;
}

.result-item strong {
	display: inline-block;
	width: 100px;
}

.success {
	color: #67c23a;
	font-weight: bold;
}

.danger {
	color: #f56c6c;
	font-weight: bold;
}

.recipient-selection {
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	padding: 15px;
	margin-bottom: 15px;
}

.selection-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
}

.selected-count {
	font-size: 14px;
	color: #606266;
}

.recipient-list {
	max-height: 200px;
	overflow-y: auto;
}

.recipient-item {
	margin-bottom: 8px;
}

.recipient-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.recipient-name {
	font-weight: bold;
	margin-right: 10px;
}

.preview-emails {
	white-space: pre-wrap;
	word-break: break-all;
}

.content-editor {
	border: 1px solid #dcdfe6;
	border-radius: 4px;
}

.editor-toolbar {
	padding: 10px;
	border-bottom: 1px solid #ebeef5;
	background-color: #fafafa;
	display: flex;
	align-items: center;
	gap: 15px;
}

.attachment-tip {
	color: #909399;
	font-size: 12px;
}

.image-preview {
	margin-top: 15px;
	padding: 15px;
	background-color: #f8f9fa;
	border-radius: 4px;
}

.image-preview-title {
	font-weight: bold;
	margin-bottom: 10px;
	color: #606266;
}

.image-list {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
}

.image-item {
	position: relative;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	padding: 5px;
	background: white;
}

.image-item img {
	max-width: 100px;
	max-height: 100px;
	object-fit: cover;
	border-radius: 2px;
}

.image-actions {
	position: absolute;
	top: 5px;
	right: 5px;
}

.attachment-list {
	margin-top: 15px;
	padding: 15px;
	background-color: #f8f9fa;
	border-radius: 4px;
}

.attachment-title {
	font-weight: bold;
	margin-bottom: 10px;
	color: #606266;
}

.attachment-item {
	display: flex;
	align-items: center;
	padding: 8px;
	margin-bottom: 5px;
	background: white;
	border-radius: 4px;
	border: 1px solid #ebeef5;
}

.attachment-item i {
	margin-right: 8px;
	color: #409EFF;
}

.attachment-name {
	flex: 1;
	margin-right: 8px;
}

.attachment-size {
	color: #909399;
	font-size: 12px;
	margin-right: 8px;
}

/* 敏感词检测样式 */
.sensitive-word-alert {
	margin-bottom: 15px;
}

.sensitive-word-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin: 10px 0;
}

.sensitive-word-tag {
	background-color: #fef0f0;
	color: #f56c6c;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 13px;
	border: 1px solid #fbc4c4;
	font-weight: 500;
}

.sensitive-word-tip {
	font-size: 13px;
	color: #f56c6c;
	margin-top: 8px;
	font-weight: 500;
}

/* 内容安全状态指示器样式 */
.content-safety-status {
	margin-top: 15px;
	padding: 12px 15px;
	border-radius: 6px;
	font-size: 14px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.checking-status {
	color: #909399;
	background-color: #f4f4f5;
	border: 1px solid #e9e9eb;
}

.safe-status {
	color: #67c23a;
	background-color: #f0f9ff;
	border: 1px solid #b3d8ff;
}

.unsafe-status {
	color: #f56c6c;
	background-color: #fef0f0;
	border: 1px solid #fbc4c4;
}

.content-safety-status i {
	font-size: 16px;
}
</style> 